import { OTLayout } from "@/components/layout/ot-layout";
import { useState, useCallback, useEffect } from "react";
import { BookOpen, ExternalLink, Plus, CheckCircle2, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const RCOT_RESOURCES = [
  {
    category: "RCOT eLearning",
    items: [
      { title: "Fatigue Management in Inflammatory Arthritis", url: "https://www.rcot.co.uk/cpd-learning", description: "RCOT accredited eLearning module on fatigue assessment and management for OTs in rheumatology." },
      { title: "Joint Protection Principles for OTs", url: "https://www.rcot.co.uk/cpd-learning", description: "Evidence-based joint protection programme delivery, including the JPPE approach for early RA." },
      { title: "Occupational Therapy Assessment in MSK Conditions", url: "https://www.rcot.co.uk/cpd-learning", description: "Standardised assessment tools including COPM, DASH, and HAQ-DI for rheumatology practice." },
    ],
  },
  {
    category: "OTnews Webinars (Rheumatology)",
    items: [
      { title: "OTnews Rheumatology Study Day — Annual", url: "https://www.rcot.co.uk/otnews", description: "Annual OTnews study day focused on rheumatology OT practice. Includes case presentations and updates." },
      { title: "Advanced Hand Therapy Webinar Series", url: "https://www.rcot.co.uk/cpd-events", description: "RCOT webinar series on therapeutic splinting, grip assessment, and hand rehabilitation in rheumatic conditions." },
      { title: "Assistive Technology in Rheumatology OT", url: "https://www.rcot.co.uk/cpd-events", description: "Practical guidance on AT prescription, DFG referrals, and digital tools for people with rheumatic conditions." },
    ],
  },
  {
    category: "RCOT CPD Resources",
    items: [
      { title: "RCOT SS Rheumatology Practice Guidelines", url: "https://www.rcot.co.uk/practice-resources", description: "Current OT-specific practice guidelines and position statements for rheumatology." },
      { title: "Evidence-Based OT: Rheumatology Resources", url: "https://www.rcot.co.uk/practice-resources", description: "Curated evidence summaries and systematic reviews relevant to rheumatology OT practice." },
      { title: "RCOT Vocational Rehabilitation Guidance", url: "https://www.rcot.co.uk/practice-resources", description: "OT guidance on supporting work participation and vocational rehabilitation for people with long-term conditions." },
      { title: "RCOT Self-Management Framework", url: "https://www.rcot.co.uk/practice-resources", description: "Framework for OT-led self-management programmes, relevant to fatigue, pain, and chronic disease management." },
    ],
  },
  {
    category: "RCOT Fellowship & Career Resources",
    items: [
      { title: "RCOT Career Development Framework", url: "https://www.rcot.co.uk/career-development", description: "The RCOT framework guiding OT career progression from proficient to consultant and beyond." },
      { title: "RCOT Fellowship (FRCOT) Information", url: "https://www.rcot.co.uk/fellowship", description: "Eligibility criteria, application process, and category information for RCOT Fellowship." },
      { title: "RCOT Leadership Development Programme", url: "https://www.rcot.co.uk/leadership", description: "Leadership training opportunities for OTs aspiring to senior clinical, academic, or strategic roles." },
    ],
  },
];

interface CpdActivity {
  id: number;
  title: string;
  date: string;
  category: string;
  hours: number;
}

interface LogFormProps {
  suggestedTitle: string;
  onSaved: () => void;
  onCancel: () => void;
}

function QuickLogForm({ suggestedTitle, onSaved, onCancel }: LogFormProps) {
  const [title, setTitle] = useState(suggestedTitle);
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [hours, setHours] = useState("1");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await fetch("/api/ot/cpd", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: title.trim(),
          date,
          category: "educational",
          hours: parseFloat(hours),
          learningNeed: "RCOT eLearning or CPD resource completion",
          activityDescription: title.trim(),
          outcome: "Completed RCOT CPD resource",
          practiceImpact: "Continued professional development in rheumatology OT",
        }),
      });
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3 mt-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-amber-800">Quick log this activity</p>
        <button type="button" onClick={onCancel} className="text-slate-400 hover:text-slate-600"><X className="h-4 w-4" /></button>
      </div>
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className="w-full h-8 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
        required
      />
      <div className="flex gap-3">
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-8 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 flex-1" />
        <div className="flex items-center gap-2">
          <input type="number" min="0.5" step="0.5" value={hours} onChange={(e) => setHours(e.target.value)} className="w-20 h-8 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500" />
          <span className="text-xs text-slate-500">hrs</span>
        </div>
        <Button type="submit" disabled={saving} size="sm" className="bg-amber-700 hover:bg-amber-800 text-white gap-1">
          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
          Log
        </Button>
      </div>
    </form>
  );
}

export default function RcotCollegeCpd() {
  const [activities, setActivities] = useState<CpdActivity[]>([]);
  const [loadingActivities, setLoadingActivities] = useState(true);
  const [quickLogFor, setQuickLogFor] = useState<string | null>(null);

  const loadActivities = useCallback(async () => {
    setLoadingActivities(true);
    try {
      const year = new Date().getFullYear();
      const res = await fetch(`/api/ot/cpd?year=${year}`);
      if (res.ok) {
        const data: CpdActivity[] = await res.json();
        setActivities(data.filter(a => a.category === "educational"));
      }
    } finally {
      setLoadingActivities(false);
    }
  }, []);

  useEffect(() => { loadActivities(); }, [loadActivities]);

  return (
    <OTLayout title="RCOT College CPD">
      <div className="space-y-6">

        <div className="bg-gradient-to-br from-amber-700 to-orange-800 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-2">
            <BookOpen className="h-7 w-7 text-amber-300" />
            <div>
              <h2 className="text-xl font-bold">RCOT College CPD Resources</h2>
              <p className="text-amber-200 text-sm">Curated RCOT learning for rheumatology occupational therapists</p>
            </div>
          </div>
          <p className="text-amber-100 text-sm leading-relaxed">
            Explore RCOT eLearning modules, OTnews webinars, and CPD resources relevant to rheumatology OT. Use the quick-log button to record completed resources directly to your CPD activity log.
          </p>
        </div>

        {/* Educational CPD Activities This Year */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-slate-900">Educational CPD Logged This Year</h3>
            <span className="text-sm text-slate-500">
              {loadingActivities ? "Loading..." : `${activities.length} activities · ${activities.reduce((s, a) => s + a.hours, 0).toFixed(1)} hrs`}
            </span>
          </div>
          {!loadingActivities && activities.length > 0 && (
            <div className="space-y-2">
              {activities.slice(0, 5).map(a => (
                <div key={a.id} className="flex items-center gap-3 px-3 py-2 bg-amber-50 rounded-lg">
                  <CheckCircle2 className="h-4 w-4 text-amber-600 shrink-0" />
                  <p className="text-sm text-slate-700 flex-1">{a.title}</p>
                  <span className="text-xs text-slate-500">{a.hours}h</span>
                </div>
              ))}
            </div>
          )}
          {!loadingActivities && activities.length === 0 && (
            <p className="text-sm text-slate-400">No educational CPD activities logged this year. Use the quick-log below to add completed RCOT learning.</p>
          )}
        </div>

        {/* Curated Resource List */}
        {RCOT_RESOURCES.map((group) => (
          <div key={group.category} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-6 py-4 bg-slate-50 border-b border-slate-200">
              <h3 className="font-bold text-slate-900">{group.category}</h3>
            </div>
            <div className="divide-y divide-slate-100">
              {group.items.map((item) => (
                <div key={item.title} className="p-5">
                  <div className="flex items-start justify-between gap-3 mb-1">
                    <h4 className="text-sm font-semibold text-slate-800">{item.title}</h4>
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-xs text-amber-700 hover:text-amber-800 shrink-0"
                    >
                      <ExternalLink className="h-3 w-3" />
                      Visit
                    </a>
                  </div>
                  <p className="text-xs text-slate-500 leading-relaxed mb-3">{item.description}</p>
                  {quickLogFor === item.title ? (
                    <QuickLogForm
                      suggestedTitle={item.title}
                      onSaved={() => { setQuickLogFor(null); loadActivities(); }}
                      onCancel={() => setQuickLogFor(null)}
                    />
                  ) : (
                    <button
                      onClick={() => setQuickLogFor(item.title)}
                      className="flex items-center gap-1.5 text-xs text-amber-700 hover:text-amber-800 bg-amber-50 hover:bg-amber-100 px-3 py-1.5 rounded-lg transition-colors font-medium"
                    >
                      <Plus className="h-3 w-3" />
                      Log as completed
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        <div className="text-xs text-slate-400 bg-slate-50 rounded-xl px-4 py-3 leading-relaxed">
          <strong>RCOT CPD Resources:</strong> Links are to the RCOT website (rcot.co.uk). Availability of specific resources may change. RCOT membership is required to access most eLearning and CPD materials. Quick-logged activities are saved to your Educational CPD category and count towards your HCPC CPD profile.
        </div>
      </div>
    </OTLayout>
  );
}
